/**
 * Main App Component
 */
import * as Sentry from '@sentry/react';
import { Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import NavigationBar from './components/NavigationBar';
import AdminDashboard from './pages/AdminDashboard';
import AssetScanPage from './pages/AssetScanPage';
import AssetsPage from './pages/AssetsPage';
import ChecklistCompletionPage from './pages/ChecklistCompletionPage';
import CodeEntryPage from './pages/CodeEntryPage';
import DonationItemScanPage from './pages/DonationItemScanPage';
import FixtureScanPage from './pages/FixtureScanPage';
import HomePage from './pages/HomePage';
import LocationScanPage from './pages/LocationScanPage';
import LogisticsDashboard from './pages/LogisticsDashboard';
import PurchaseOrderListPage from './pages/PurchaseOrderListPage';
import PurchaseOrderPage from './pages/PurchaseOrderPage';
import ScanPage from './pages/ScanPage';
import SIGDashboard from './pages/SIGDashboard';
import ThanksPage from './pages/ThanksPage';
import TransparencyPage from './pages/TransparencyPage';
import TaxReceiptLookupPage from './pages/TaxReceiptLookupPage';
import TVDashboard from './pages/TVDashboard';
import './styles/App.css';

// Wrap routes with Sentry for better error tracking
const SentryRoutes = Sentry.withSentryRouting(Routes);

// Component to conditionally show NavigationBar
function AppContent() {
  const location = useLocation();
  
  // Hide navigation bar on TV Dashboard and Logistics Dashboard routes
  const shouldShowNavBar = !location.pathname.startsWith('/tv-dashboard') && 
                           !location.pathname.startsWith('/tv-logistics');
  
  return (
    <div className="App">
      {shouldShowNavBar && <NavigationBar />}
      <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <h1>Something went wrong</h1>
              <p>{error.message}</p>
              <button onClick={resetError}>Try again</button>
            </div>
          )}
          showDialog
        >
          <SentryRoutes>
            <Route path="/" element={<HomePage />} />
            <Route path="/assets" element={<AssetsPage />} />
            <Route path="/scan/:itemId" element={<ScanPage />} />
            <Route path="/scan/fixture/:fixtureId" element={<FixtureScanPage />} />
            <Route path="/scan/asset/:assetId" element={<AssetScanPage />} />
            <Route path="/scan/location/:locationId" element={<LocationScanPage />} />
            <Route path="/scan/donation-item/:itemId" element={<DonationItemScanPage />} />
            <Route path="/checklist/:checklistId/complete/:completionId" element={<ChecklistCompletionPage />} />
            <Route path="/code-entry" element={<CodeEntryPage />} />
            <Route path="/orderadmin" element={<AdminDashboard />} />
            <Route path="/thanks" element={<ThanksPage />} />
            <Route path="/tv-dashboard" element={<TVDashboard />} />
            <Route path="/tv-dashboard/:location" element={<TVDashboard />} />
            <Route path="/tv-logistics" element={<LogisticsDashboard />} />
            <Route path="/transparency" element={<TransparencyPage />} />
            <Route path="/purchase-order" element={<PurchaseOrderListPage />} />
            <Route path="/purchase-order/:orderId" element={<PurchaseOrderPage />} />
            <Route path="/sig-dashboard" element={<SIGDashboard />} />
            <Route path="/sig-dashboard/:sigId" element={<SIGDashboard />} />
            <Route path="/tax-receipt/lookup" element={<TaxReceiptLookupPage />} />
          </SentryRoutes>
        </Sentry.ErrorBoundary>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
